/**
 * Header + search-page product lookup.
 * Uses Shopify suggest.json (and products.json as backup) so typed titles
 * still resolve when the storefront search index misses.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 180;
  var timers = new WeakMap();

  function predictiveJsonUrl() {
    var base = (window.Theme && Theme.routes && Theme.routes.predictive_search_url) || '/search/suggest';
    if (/\.json$/i.test(base)) return base;
    return String(base).replace(/\/?$/, '') + '.json';
  }

  function searchPageUrl() {
    return (window.Theme && Theme.routes && Theme.routes.search_url) || '/search';
  }

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokens(value) {
    return normalize(value).split(' ').filter(function (t) {
      return t.length > 0;
    });
  }

  function haystack(product) {
    var parts = [product.title, product.handle, product.vendor, product.type, product.product_type];
    if (product.tags) parts.push(Array.isArray(product.tags) ? product.tags.join(' ') : product.tags);
    return normalize(parts.filter(Boolean).join(' '));
  }

  function scoreProduct(product, query) {
    var q = normalize(query);
    if (!q) return 0;
    var title = normalize(product.title);
    var hay = haystack(product);
    var tks = tokens(q);
    if (!tks.length) return 0;

    if (title === q) return 1000;
    if (title.indexOf(q) === 0) return 900;
    if (title.indexOf(q) !== -1) return 800;

    var all = true;
    var i;
    for (i = 0; i < tks.length; i++) {
      if (hay.indexOf(tks[i]) === -1) {
        all = false;
        break;
      }
    }
    if (all) return 700 - tks.length;

    var some = 0;
    for (i = 0; i < tks.length; i++) {
      if (tks[i].length >= 2 && hay.indexOf(tks[i]) !== -1) some += 40;
    }
    return some;
  }

  function uniqueByUrl(list) {
    var seen = Object.create(null);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var p = list[i];
      var key = p.url || p.handle || p.title;
      if (!key || seen[key]) continue;
      seen[key] = true;
      out.push(p);
    }
    return out;
  }

  function mapSuggestProduct(p) {
    return {
      title: p.title,
      url: p.url,
      handle: p.handle,
      vendor: p.vendor,
      type: p.product_type || p.type,
      featured_image: p.featured_image || (p.image && (p.image.url || p.image.src)) || p.image
    };
  }

  function mapAjaxProduct(p) {
    var img = p.featured_image || (p.images && p.images[0]) || '';
    if (img && typeof img === 'object') img = img.src || img.url || '';
    return {
      title: p.title,
      url: '/products/' + p.handle,
      handle: p.handle,
      vendor: p.vendor,
      type: p.product_type,
      tags: p.tags,
      featured_image: img
    };
  }

  function fetchSuggest(query) {
    var url = new URL(predictiveJsonUrl(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    return fetch(url.toString(), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        return products.map(mapSuggestProduct);
      })
      .catch(function () {
        return [];
      });
  }

  function fetchCatalog(query) {
    return fetch('/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) return [];
        return res.json();
      })
      .then(function (data) {
        var products = (data && data.products) || [];
        return products.map(mapAjaxProduct);
      })
      .catch(function () {
        return [];
      });
  }

  function rankProducts(products, query) {
    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var s = scoreProduct(products[i], query);
      if (s > 0) scored.push({ product: products[i], score: s });
    }
    scored.sort(function (a, b) {
      return b.score - a.score;
    });
    return scored.map(function (row) {
      return row.product;
    });
  }

  function searchProducts(query) {
    var q = String(query || '').trim();
    if (!q) return Promise.resolve([]);
    return Promise.all([fetchSuggest(q), fetchCatalog(q)]).then(function (parts) {
      var merged = uniqueByUrl(parts[0].concat(parts[1]));
      return rankProducts(merged, q).slice(0, 10);
    });
  }

  function imageUrl(src) {
    if (!src) return '';
    if (typeof src === 'object') src = src.url || src.src || '';
    return String(src);
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function viewAllUrl(query) {
    var url = new URL(searchPageUrl(), window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('options[unavailable_products]', 'last');
    return url.toString();
  }

  function renderList(container, products, query) {
    if (!container) return;
    if (!products.length) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }
    var label = container.getAttribute('data-view-all-label') || 'View all products';
    var html = '<ul class="zoony-search-suggest__list" role="listbox">';
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      var img = imageUrl(p.featured_image);
      html +=
        '<li>' +
        '<a class="zoony-search-suggest__item" href="' +
        escapeHtml(p.url) +
        '" role="option">' +
        (img
          ? '<img src="' + escapeHtml(img) + '" alt="" width="48" height="48">'
          : '<span class="zoony-search-suggest__ph" aria-hidden="true"></span>') +
        '<span class="zoony-search-suggest__text">' +
        escapeHtml(p.title) +
        '</span></a></li>';
    }
    html += '</ul>';
    html +=
      '<a class="zoony-search-suggest__all" href="' +
      escapeHtml(viewAllUrl(query)) +
      '">' +
      '<span>' +
      escapeHtml(label) +
      '</span>' +
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M5 12h14"/><path d="M13 6l6 6-6 6"/>' +
      '</svg></a>';
    container.innerHTML = html;
    container.hidden = false;
  }

  function bindForm(form) {
    var input = form.querySelector('input[name="q"]');
    var panel = form.querySelector('[data-zoony-search-suggest]');
    if (!input || !panel) return;

    function run() {
      var q = input.value.trim();
      if (q.length < 2) {
        panel.hidden = true;
        panel.innerHTML = '';
        return;
      }
      searchProducts(q).then(function (products) {
        if (input.value.trim() !== q) return;
        renderList(panel, products, q);
      });
    }

    input.addEventListener('input', function () {
      var prev = timers.get(form);
      if (prev) window.clearTimeout(prev);
      timers.set(
        form,
        window.setTimeout(run, DEBOUNCE_MS)
      );
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2) run();
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) {
        panel.hidden = true;
      }
    });
  }

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  onReady(function () {
    document.querySelectorAll('[data-zoony-search-form]').forEach(bindForm);
  });
})();
